import { useState, useEffect, useRef } from 'react';
import { AppState, AppStateStatus } from 'react-native';

/**
 * Hook to track app state changes (active, background, inactive)
 * Useful for optimizing polling and preventing unnecessary API calls
 */
export const useAppState = () => {
  const [appState, setAppState] = useState<AppStateStatus>(AppState.currentState);

  useEffect(() => {
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      setAppState(nextAppState);
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);

    return () => {
      subscription?.remove();
    };
  }, []);

  return {
    appState,
    isActive: appState === 'active',
    isBackground: appState === 'background',
    isInactive: appState === 'inactive',
  };
};

/**
 * Hook for optimized polling that respects app state
 * Automatically pauses when app goes to background
 */
export const useOptimizedPolling = (
  callback: () => void,
  interval: number,
  enabled: boolean = true
) => {
  const { isActive } = useAppState();
  const callbackRef = useRef(callback);

  // Update the ref whenever callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || !isActive) return;

    // Initial call
    callbackRef.current();

    // Set up interval
    const intervalId = setInterval(() => {
      if (isActive) {
        callbackRef.current();
      }
    }, interval);

    return () => {
      clearInterval(intervalId);
    };
  }, [interval, enabled, isActive]); // Remove callback from dependencies
};
